/**
 * Target vehicle info chassis list behaviors:
 *
 * 1. Linkifies chassis items the server could not match. Liquid can only scan
 *    the first 250 collections (paginate cap) and may miss items when page
 *    copy and collection titles differ (extra words, year ranges). This
 *    fallback fetches the full collection list and matches by significant
 *    tokens in the title and handle, so titles with interleaved words
 *    (e.g. "FALCON Ford Ute ..." vs item "FALCON UTE ...") still match.
 * 2. Persists the clicked chassis as the active MMY vehicle context
 *    (localStorage + cookies), mirroring component-search-form-native.js /
 *    mmy-state.js, so collection and product pages match the chosen chassis.
 */
(function targetVehicleInfo() {
  const MMY_STORAGE_KEY = 'mmy';
  const COOKIE_MAX_AGE = 'max-age=604800;path=/;SameSite=Lax';
  const COLLECTIONS_CACHE_KEY = 'target-vehicle-info-collections-v2';
  const COLLECTIONS_CACHE_TTL_MS = 1000 * 60 * 60 * 24;
  const COLLECTIONS_PAGE_SIZE = 250;
  const COLLECTIONS_MAX_PAGES = 10;

  /**
   * @param {string} value
   * @returns {string}
   */
  function handleize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&[a-z]+;|&#\d+;/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * @param {string} value
   * @returns {string[]}
   */
  function tokenize(value) {
    return handleize(value).split('-').filter(Boolean);
  }

  /**
   * @returns {{ title: string, handle: string }[] | null}
   */
  function readCollectionsCache() {
    try {
      const raw = localStorage.getItem(COLLECTIONS_CACHE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.collections)) return null;
      if (Date.now() - Number(parsed.savedAt || 0) > COLLECTIONS_CACHE_TTL_MS) return null;

      return parsed.collections;
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {{ title: string, handle: string }[]} collections
   */
  function writeCollectionsCache(collections) {
    try {
      localStorage.setItem(
        COLLECTIONS_CACHE_KEY,
        JSON.stringify({ savedAt: Date.now(), collections }),
      );
    } catch (error) {
      // Ignore storage errors (private mode, quota, etc).
    }
  }

  /**
   * @returns {Promise<{ title: string, handle: string }[]>}
   */
  async function loadAllCollections() {
    const cached = readCollectionsCache();
    if (cached) return cached;

    /** @type {{ title: string, handle: string }[]} */
    const collections = [];

    try {
      for (let page = 1; page <= COLLECTIONS_MAX_PAGES; page += 1) {
        const url = new URL('/collections.json', window.location.origin);
        url.searchParams.set('limit', String(COLLECTIONS_PAGE_SIZE));
        url.searchParams.set('page', String(page));

        const response = await fetch(url.toString());
        if (!response.ok) break;

        const json = await response.json();
        const batch = Array.isArray(json.collections) ? json.collections : [];

        for (const collection of batch) {
          collections.push({
            title: String(collection.title || ''),
            handle: String(collection.handle || ''),
          });
        }

        if (batch.length < COLLECTIONS_PAGE_SIZE) break;
      }
    } catch (error) {
      console.error('[target-vehicle-info] Failed to load collections', error);
    }

    if (collections.length) writeCollectionsCache(collections);
    return collections;
  }

  /**
   * MMY collection handles are prefixed with a numeric chassis group id.
   *
   * @param {string} handle
   * @returns {string}
   */
  function stripCollectionHandlePrefix(handle) {
    return String(handle || '').replace(/^\d+-/, '');
  }

  /**
   * Year fragments (07, 11, 19, etc.) often differ between page copy and
   * collection titles, so they are scored but not required for a match.
   *
   * @param {string} token
   * @returns {boolean}
   */
  function isOptionalMatchToken(token) {
    return /^\d{1,2}$/.test(token);
  }

  /**
   * @param {string} value
   * @returns {string[]}
   */
  function getSignificantTokens(value) {
    return tokenize(value).filter((token) => !isOptionalMatchToken(token));
  }

  /**
   * @param {{ title: string, handle: string }} collection
   * @returns {Set<string>}
   */
  function getCollectionMatchTokens(collection) {
    return new Set([
      ...tokenize(collection.title),
      ...tokenize(stripCollectionHandlePrefix(collection.handle)),
    ]);
  }

  /**
   * @param {string} itemText
   * @param {{ title: string, handle: string }[]} collections
   * @returns {{ title: string, handle: string } | null}
   */
  function findCollectionForItem(itemText, collections) {
    const itemHandle = handleize(itemText);
    if (!itemHandle) return null;

    const itemTokens = tokenize(itemText);
    const significantItemTokens = getSignificantTokens(itemText);

    /** @type {{ collection: { title: string, handle: string }, score: number } | null} */
    let bestMatch = null;

    for (const collection of collections) {
      const titleHandle = handleize(collection.title);
      const strippedHandle = stripCollectionHandlePrefix(collection.handle);
      const candidateTokens = getCollectionMatchTokens(collection);

      // Exact substring match on title or stripped handle wins immediately.
      if (titleHandle.includes(itemHandle) || strippedHandle.includes(itemHandle)) {
        return collection;
      }

      if (!significantItemTokens.length) continue;

      const significantMatch = significantItemTokens.every((token) => candidateTokens.has(token));
      if (!significantMatch) continue;

      const matchedTokenCount = itemTokens.filter((token) => candidateTokens.has(token)).length;
      const extraTokenCount = [...candidateTokens].filter((token) => !itemTokens.includes(token)).length;
      const score = matchedTokenCount * 100 - extraTokenCount;

      if (!bestMatch || score > bestMatch.score) {
        bestMatch = { collection, score };
      }
    }

    return bestMatch ? bestMatch.collection : null;
  }

  /**
   * @param {HTMLElement} staticItem
   * @param {string} handle
   * @param {string} itemText
   */
  function upgradeStaticItemToLink(staticItem, handle, itemText) {
    const link = document.createElement('a');
    link.className = 'target-vehicle-info__chassis-link';
    link.href = `/collections/${handle}`;
    link.dataset.mmyChassisLabel = itemText;

    while (staticItem.firstChild) {
      link.appendChild(staticItem.firstChild);
    }

    staticItem.replaceWith(link);
  }

  async function linkifyUnmatchedItems() {
    const staticItems = [
      ...document.querySelectorAll(
        '[data-target-vehicle-info] .target-vehicle-info__chassis-link--static',
      ),
    ].filter((node) => node instanceof HTMLElement);

    if (!staticItems.length) return;

    const collections = await loadAllCollections();
    if (!collections.length) return;

    for (const staticItem of staticItems) {
      const label = staticItem.querySelector('.target-vehicle-info__chassis-label');
      const itemText = (label || staticItem).textContent?.trim() || '';
      if (!itemText) continue;

      const collection = findCollectionForItem(itemText, collections);
      if (!collection) continue;

      upgradeStaticItemToLink(staticItem, collection.handle, itemText);
    }
  }

  /**
   * @param {string} name
   * @param {string} value
   */
  function setCookie(name, value) {
    document.cookie = `${name}=${encodeURIComponent(value)};${COOKIE_MAX_AGE}`;
  }

  /**
   * @param {string} name
   */
  function clearCookie(name) {
    document.cookie = `${name}=;max-age=0;path=/;SameSite=Lax`;
  }

  /**
   * @param {string} href
   * @returns {string}
   */
  function collectionHandleFromHref(href) {
    try {
      const url = new URL(href, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const index = parts.indexOf('collections');
      if (index !== -1 && parts[index + 1]) return parts[index + 1];
    } catch (error) {
      // Ignore parse issues; the chassis label alone is still useful.
    }
    return '';
  }

  /**
   * @param {MouseEvent} event
   */
  function handleChassisClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a.target-vehicle-info__chassis-link');
    if (!(link instanceof HTMLAnchorElement)) return;

    const root = link.closest('[data-target-vehicle-info]');
    if (!(root instanceof HTMLElement)) return;

    const label = String(link.dataset.mmyChassisLabel || '').trim();
    if (!label) return;

    const make = String(root.dataset.mmyMake || '').trim();
    const model = String(root.dataset.mmyModel || '').trim();
    const collectionHandle = collectionHandleFromHref(link.href);

    // MMY collection handles are prefixed with the numeric chassis group id
    // (e.g. "999800016-air-bag-suspension-for-..."), the same id the vehicle
    // search stores. Extract it so the saved context is fully equivalent.
    const groupIdMatch = collectionHandle.match(/^\d{5,}/);
    const chassisGroupId = groupIdMatch ? groupIdMatch[0] : '';

    const state = {
      selmake: make,
      selmodel: model,
      // Without a real group id, fall back to a non-numeric placeholder:
      // keeps selchassislabel readable by mmy-state.js pickMmySelections
      // (isMmyChassisGroupId filters non-numeric values out).
      selchassisgroup: chassisGroupId || collectionHandle || label,
      selchassislabel: label,
      selheights: '',
    };

    try {
      localStorage.setItem(MMY_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Private mode / quota errors: cookies below still carry the context.
    }

    if (make) {
      setCookie('mmy_make', make);
    } else {
      clearCookie('mmy_make');
    }

    if (model) {
      setCookie('mmy_model', model);
    } else {
      clearCookie('mmy_model');
    }

    setCookie('mmy_chassis', label);

    if (chassisGroupId) {
      setCookie('mmy_cg', chassisGroupId);
    } else {
      clearCookie('mmy_cg');
    }

    clearCookie('mmy_height');
  }

  document.addEventListener('click', handleChassisClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', linkifyUnmatchedItems);
  } else {
    linkifyUnmatchedItems();
  }
})();
